// import { useQuery } from 'react-query';
import { useAsync } from 'react-use-v13';
import apiFetch from '@scale/frontend-lib/api-fetch';

interface IPodStatsQuery {
  podId: string;
}

class PodStatsBundler {
  _buffer: IPodStatsQuery[] = [];
  _dataMap: Record<string, Promise<Record<string, number>>> = {};
  _resolvers: Record<string, [(obj: Record<string, number>) => void, (err: any) => void]> = {};
  _debounceTime: number;
  _maxBatchSize: number;
  _lastUpdate = 0;
  _timer: ReturnType<typeof setTimeout> | null = null;
  constructor({
    debounceTime,
    maxBatchSize,
  }: { debounceTime?: number; maxBatchSize?: number } = {}) {
    this._debounceTime = debounceTime || 500;
    this._maxBatchSize = maxBatchSize || 50;
  }

  private async _batchedDebouncedFetch() {
    if (this._timer) {
      clearTimeout(this._timer);
    }
    const now = new Date().getTime();
    if (this._lastUpdate === 0) this._lastUpdate = now - this._debounceTime + 50; // Allow slight buffer to allow collecting first batch
    const targetUpdateTime = this._lastUpdate + this._debounceTime;
    if (targetUpdateTime > now) {
      // Fetch later
      this._timer = setTimeout(() => this._batchedDebouncedFetch(), targetUpdateTime - now);
      return;
    }
    // Fetch now
    this._lastUpdate = now;
    const batch = this._buffer.splice(0, this._maxBatchSize);
    if (!batch.length) return;

    const { objects } = await apiFetch<{ objects: Record<string, Record<string, number> | null> }>(
      null,
      `internal/expert-management/batchPodStats`,
      {
        method: 'POST',
        body: { batch },
      },
    );

    // Resolve promises
    for (const [id, object] of Object.entries(objects)) {
      if (!this._resolvers[id]) continue;
      const [resolve, reject] = this._resolvers[id];
      if (!object) reject(object);
      else resolve(object);
    }

    // If there still objects to lookup, fetch now...
    this._batchedDebouncedFetch();
  }

  async fetch(podId: string): Promise<Record<string, number>> {
    // Is the result cached?
    if (podId in this._dataMap) return this._dataMap[podId];

    // Schedule lookup
    this._buffer.push({ podId });
    this._dataMap[podId] = new Promise((resolve, reject) => {
      this._resolvers[podId] = [resolve, reject];
    });
    this._batchedDebouncedFetch();
    return this._dataMap[podId];
  }
}

const bundler = new PodStatsBundler();

export const useFetchPodStats = (podName: string, podId: string) => {
  return useAsync(() => bundler.fetch(podId), [podId]);
};
